import { TabContext, TabPanel } from '@mui/lab';
import { Stack, Tab, Typography } from '@mui/material';
import { grey } from '@mui/material/colors';
import React from 'react';
import { CutterHistory } from '../../types/cutterHistory';
import CutterDetailsHistoryContact from './CutterDetailsHistory.contactinfo';
import CutterDetailsHistorySalesforceCases from './CutterDetailsHistory.salesforcecases';
import CutterDetailsHistorySubscriptions from './CutterDetailsHistory.subscriptions';
import HelpIcon from '../../help/HelpIcon';
import TabsWithExport from '../../common/TabsWithExport';

export interface CutterDetailsHistoryPaperworkProps {
    cutter: CutterHistory;
    isVersalis: boolean;
    horizontalTabValue: string;
    onHorizontalTabChange: (event: React.SyntheticEvent, newValue: string) => void;
    commonDataGridProps: any;
    horizontalTabsProps: any;
    tabPanelSx: any;
}

export const CutterDetailsHistoryPaperwork: React.FC<CutterDetailsHistoryPaperworkProps> = ({
    cutter,
    horizontalTabValue,
    onHorizontalTabChange,
    horizontalTabsProps,
}) => {

    // Derive active dataset + ExcelJS columns from the visible tab
    const activeExport = React.useMemo(() => {
        const dataToExcelCols = (data: any[]): Array<{ key: string; header: string }> =>
            data.length === 0 ? [] : Object.keys(data[0]).map(k => ({ key: k, header: k }));
        const filteredContracts = cutter.contract.filter(s => s.contract);
        switch (horizontalTabValue) {
            case 'salesforce':
                return { data: cutter.salesforceCases, columns: dataToExcelCols(cutter.salesforceCases), label: `${cutter.serialNumber} salesforce-cases` };
            case 'contact':
                return { data: cutter.contacts, columns: dataToExcelCols(cutter.contacts), label: `${cutter.serialNumber} contacts` };
            case 'subscriptions':
                return { data: filteredContracts, columns: dataToExcelCols(filteredContracts), label: `${cutter.serialNumber} subscriptions` };
            default:
                return null;
        }
    }, [horizontalTabValue, cutter]);

    return (
        <TabContext value={horizontalTabValue}>
            <TabsWithExport
                value={horizontalTabValue}
                onChange={onHorizontalTabChange}
                {...horizontalTabsProps}
                sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[100] : '#252525'
                }}
                aria-label="Paperwork tabs"
                exportData={activeExport ? { ...activeExport, exportType: 'paperwork' } : null}>
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Open Cases ({cutter.salesforceCases.length > 0 ? cutter.salesforceCases.length : 'None'})</Typography>
                        <HelpIcon helpContext="details-salesforce-cases" placement="right" size="small" />
                    </Stack>
                } value='salesforce' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Contacts ({(cutter.contacts.length > 0 ? cutter.contacts.length : 'No Data')})</Typography>
                        <HelpIcon helpContext="details-contacts" placement="right" size="small" />
                    </Stack>
                } value='contact' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Subscriptions ({cutter.contract.filter(subscr => subscr.contract && subscr.isactive).length})</Typography>
                        <HelpIcon helpContext="details-subscriptions" placement="right" size="small" />
                    </Stack>
                } value='subscriptions' />
            </TabsWithExport>
            <TabPanel value='salesforce' sx={{ p: 0, color: 'disable' }}>
                <CutterDetailsHistorySalesforceCases cutter={cutter} />
            </TabPanel>
            <TabPanel value='contact' sx={{ p: 0, color: 'disable' }}>
                <CutterDetailsHistoryContact cutter={cutter} />
            </TabPanel>
            <TabPanel value='subscriptions' sx={{ p: 2 }}>
                <CutterDetailsHistorySubscriptions cutter={cutter} />
            </TabPanel>
        </TabContext>
    );
};

export default CutterDetailsHistoryPaperwork;